import { defineAsyncComponent, Component } from 'vue'

// Pre-cargar todos los componentes disponibles usando import.meta.glob
// Vite resolverá estos globs en tiempo de compilación
const brandModules = import.meta.glob<any>('../../../brand/src/**/*.vue')
const coreModules = import.meta.glob<any>('/src/**/*.vue')

// Normalizar las keys de los módulos para facilitar el lookup
const brandComponents: Record<string, () => Promise<any>> = {}
const coreComponents: Record<string, () => Promise<any>> = {}

// Normalizar paths de brand: ../../../brand/src/components/... → components/...
Object.keys(brandModules).forEach(path => {
  const normalizedPath = path.replace('../../../brand/src/', '')
  brandComponents[normalizedPath] = brandModules[path]
})

// Normalizar paths de core: /src/components/... → components/...
Object.keys(coreModules).forEach(path => {
  const normalizedPath = path.replace('/src/', '')
  coreComponents[normalizedPath] = coreModules[path]
})

// Debug: Ver qué componentes se detectaron
console.log('[useBrandComponent] Brand components detected:', Object.keys(brandComponents).length)
console.log('[useBrandComponent] Core components detected:', Object.keys(coreComponents).length)
if (Object.keys(brandComponents).length > 0) {
  console.log('[useBrandComponent] Sample brand paths:', Object.keys(brandComponents).slice(0, 3))
}

/**
 * Composable para cargar componentes con fallback brand → core
 * 
 * Orden de carga:
 * 1. Intenta cargar de brand/{componentPath}
 * 2. Si no existe, carga de core/{componentPath}
 * 3. Si ninguno existe, lanza error
 * 
 * @param componentPath - Ruta relativa del componente (ej: 'components/common/Button.vue')
 * @returns Component - Componente asíncrono listo para usar
 * 
 * @example
 * const MyButton = useBrandComponent('components/common/Button.vue')
 * const DataTable = useBrandComponent('components/modules/DataTable.vue')
 */
export function useBrandComponent(componentPath: string): Component {
  return defineAsyncComponent(async () => {
    // Intentar cargar de brand primero
    if (brandComponents[componentPath]) {
      try {
        const brandComponent = await brandComponents[componentPath]()
        console.log(`✅ [Brand] Component loaded: ${componentPath}`)
        return brandComponent.default || brandComponent
      } catch (brandError) {
        console.warn(`⚠️ [Brand] Component found but failed to load: ${componentPath}`, brandError)
      }
    }
    
    // Fallback a core
    if (coreComponents[componentPath]) {
      try {
        const coreComponent = await coreComponents[componentPath]()
        console.log(`ℹ️ [Core] Component loaded: ${componentPath}`)
        return coreComponent.default || coreComponent
      } catch (coreError) {
        console.error(`❌ [Core] Component found but failed to load: ${componentPath}`, coreError)
        throw coreError
      }
    }
    
    // Si no existe en ninguno
    console.error(`❌ Component not found in brand or core: ${componentPath}`)
    console.info('Tried to load:', componentPath)
    console.info('Available brand components:', Object.keys(brandComponents).slice(0, 5))
    console.info('Available core components (sample):', Object.keys(coreComponents).slice(0, 5), '...')
    throw new Error(`Component not found: ${componentPath}`)
  })
}

/**
 * Variante para cargar múltiples componentes a la vez
 * 
 * @example
 * const { Button, Input, Modal } = useBrandComponents({
 *   Button: 'components/common/Button.vue',
 *   Input: 'components/common/Input.vue',
 *   Modal: 'components/common/Modal.vue'
 * })
 */
export function useBrandComponents<T extends Record<string, string>>(
  components: T
): Record<keyof T, Component> {
  const result = {} as Record<keyof T, Component>
  
  for (const [key, path] of Object.entries(components)) {
    result[key as keyof T] = useBrandComponent(path)
  }
  
  return result
}
